import { Icon } from '../../../components/icons';
import { FeaturePageLayout, type FeatureContent } from './FeaturePageLayout';
import { PhoneValidationStory } from './stories/PhoneValidationStory';

const CONTENT: FeatureContent = {
  kind: 'phone_validation',
  slug: 'phone-validation',
  story: <PhoneValidationStory />,
  title: 'Phone Validation',
  tagline: 'Catch VoIP & toll-free numbers before they pollute your SMS campaigns.',
  metaDescription:
    'Validate phone numbers at signup: carrier lookup, line type (mobile, VoIP or landline), country and reachability. Real-time API plus bulk — catch junk early.',
  eyebrow: 'Credit-metered · Pro-bundled headroom',
  description:
    "Look up any phone number's country, carrier, and line type (mobile / VoIP / fixed line / toll-free / premium-rate / shared-cost). Cache-first — repeat lookups of the same number cost zero credits. Bulk + single modes.",
  icon: <Icon.User width={48} height={48} />,
  accent: 'allow',
  heroBg: '/heroes/feat-phone-validation.webp',
  pillars: [
    {
      title: 'Line-type intelligence',
      body: 'Mobile = SMS-deliverable. VoIP, toll-free, fixed-line = silently fail. Premium-rate = paid services. The line-type field is the single most important signal for SMS-campaign teams; we surface it with a sensible-defaults UI explaining what each one means.',
    },
    {
      title: 'Cache-first economics',
      body: 'Re-lookups of the same E.164 number return from the workspace cache for free. Bulk uploads explode into the cache so subsequent single-lookups are also free. A tenant who re-imports the same CRM monthly pays once.',
    },
    {
      title: 'Bulk + single',
      body: 'Single-number verify from the Console-style toolbar; CSV-bulk for fleet imports (up to 1M rows). Progress reflects real upstream lookup state, not a fake queue indicator.',
    },
  ],
  steps: [
    {
      title: 'Submit a number',
      body: 'Through the in-app Console (Enter to verify), the CSV bulk uploader, or the API at POST /v1/phone-validation/verify with E.164 or any format we can parse.',
    },
    {
      title: 'We canonicalise',
      body: 'We normalise to E.164 and resolve country + carrier + line-type. Syntax-error rows are returned free (no credit consumed).',
    },
    {
      title: 'Cache the result',
      body: 'Stored in the workspace lookup cache keyed by (tenant, E.164). Every subsequent lookup of the same number — single OR via a bulk upload — returns for free.',
    },
    {
      title: 'Surface it everywhere',
      body: 'Per-number detail page with SMS-deliverability hint based on the line type; analytics tab with a "SMS-deliverable share" tile (mobile vs everything else); cached lookups browser with status / type / country filters.',
    },
  ],
  included: [
    {
      scope: 'trial',
      items: [
        'A few lookups to validate the workflow',
        'Single-number + cache-first path',
        'Per-lookup detail page',
      ],
    },
    {
      scope: 'pro',
      items: [
        'Bundled free on every Pro plan',
        'Pro 10k+ bundles a larger monthly quota',
        'Repeat lookups always free regardless of tier',
      ],
    },
    {
      scope: 'addon',
      items: [
        'Top up with a credit pack when your monthly allowance runs low',
        'Bulk upload up to 1M rows in one job',
        'Repeat lookups return from cache for free — pay once per number per workspace',
      ],
    },
  ],
  faq: [
    {
      q: 'How is this different from Twilio Lookup?',
      a: 'SieveGuard caches every result per-workspace, so re-lookups are zero-cost — common when you re-import the same CRM month over month. Other lookup APIs typically charge per request regardless of whether you\'ve seen the number before; we charge once.',
    },
    {
      q: 'What does "syntax-error" mean and is it billed?',
      a: 'Numbers we can\'t parse at all (e.g. random strings, too few digits) come back with status=syntax-error and are NOT billed. They\'re still stored in the cache so you don\'t re-submit the same malformed row twice.',
    },
    {
      q: 'How accurate is the line-type field?',
      a: 'It is based on number-portability databases aggregated per country. Pretty reliable in mature markets (US / UK / DE / FR / NL); slightly less so in emerging markets where porting databases are less complete. The "unknown" status is honest about that — it means "we cannot classify confidently" rather than guessing.',
    },
  ],
  addonKind: 'phone_validation',
  relatedFeatures: [
    {
      slug: 'inbox-placement',
      title: 'Inbox Placement',
      tagline: 'The email-side equivalent: see where mail lands instead of guessing.',
    },
    {
      slug: 'domain-health',
      title: 'Domain Health',
      tagline: 'Email-side authentication posture, free with every Pro plan.',
    },
    {
      slug: 'warmup',
      title: 'Warmup',
      tagline: 'Build sender reputation across your sending mailboxes.',
    },
  ],
};

export function PhoneValidationFeaturePage() {
  return <FeaturePageLayout content={CONTENT} />;
}

// Default export required by React Router framework-mode route modules.
export default PhoneValidationFeaturePage;
